import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link, withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import cookie from 'react-cookies';
import * as actions from '../../../features/home/redux/actions';
import * as tutorialActions from '../../../features/tutorial/redux/actions';
import { getBuilderURL } from '../../../common/utils/builder-url-util';
import PageTab from '../sidebar/PageTab';
import EventTab from '../sidebar/EventTab';
import CollectionTab from '../sidebar/CollectionTab';
import SnippetTemplateTab from '../sidebar/SnippetTemplateTab';
import AssetTab from '../sidebar/AssetTab';
import { WorkspaceChromeContext } from './WorkspaceChromeContext';
import { getShellNavigation, isNavItemActive } from './navConfig';

export class ProjectSidebar extends Component {
  static propTypes = {
    projectId: PropTypes.string.isRequired,
    version: PropTypes.string,
    tutorialActions: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
  };

  static contextType = WorkspaceChromeContext;

  state = {
    openFlyout: '',
    openMenu: '',
  };

  componentDidUpdate(prevProps) {
    const previous = prevProps.location && prevProps.location.pathname;
    const next = this.props.location && this.props.location.pathname;
    if (previous !== next && this.context && this.context.closeNav) {
      this.context.closeNav();
    }
  }

  openEditor = () => {
    const { projectId, version } = this.props;
    let token = cookie.load('token');
    if (!token) token = '';
    const redirectUrl = getBuilderURL();
    window.location.href = `${redirectUrl}editor/${version}/v/${projectId}/loader?token=${token}`;
  };

  toggleSidebar = (event, name) => {
    const sideBarToVisible = document.getElementById(name);
    if (!sideBarToVisible) return;
    const alreadySelected = sideBarToVisible.classList.contains('selected');
    const panels = document.getElementsByClassName('sidebar');
    for (let i = 0; i < panels.length; i += 1) {
      panels[i].classList.remove('selected');
    }
    if (!alreadySelected) {
      sideBarToVisible.classList.add('selected');
      this.setState({ openFlyout: name });
    } else {
      this.setState({ openFlyout: '' });
    }
    if (event && event.currentTarget) {
      event.currentTarget.blur();
    }
  };

  toggleSearch = () => {
    window.dispatchEvent(new CustomEvent('toggleGlobalSearch', { detail: { visible: true } }));
  };

  toggleMenu = (id) => {
    this.setState((prev) => ({ openMenu: prev.openMenu === id ? '' : id }));
  };

  startResize = (event) => {
    if (!this.context || this.context.sidebarCollapsed || !this.context.setSidebarWidth) return;
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = this.context.sidebarWidth || 240;
    const move = (pointerEvent) => {
      this.context.setSidebarWidth(startWidth + (pointerEvent.clientX - startX));
    };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
  };

  handleItem = (item) => {
    if (item.action === 'editor') this.openEditor();
    if (item.action === 'search') this.toggleSearch();
    if (item.action === 'tutorials' && this.props.tutorialActions) {
      this.props.tutorialActions.toggleTutorialModal();
    }
  };

  renderLink = (item, pathname) => {
    const active = isNavItemActive(pathname, item) || this.state.openFlyout === item.flyoutId;
    const className = `dc-nav-item${active ? ' is-active' : ''}`;
    const icon = <i className={`wb ${item.icon}`} aria-hidden="true" />;
    const label = <span className="dc-nav-label">{item.label}</span>;

    if (item.action === 'flyout') {
      const expanded = this.state.openFlyout === item.flyoutId;
      return (
        <button
          key={item.id}
          type="button"
          className={className}
          title={item.label}
          aria-expanded={expanded}
          onClick={(event) => this.toggleSidebar(event, item.flyoutId)}
        >
          {icon}
          {label}
        </button>
      );
    }

    if (item.to) {
      return (
        <Link
          key={item.id}
          to={item.to}
          className={className}
          title={item.label}
          aria-current={active ? 'page' : undefined}
        >
          {icon}
          {label}
        </Link>
      );
    }

    if (item.href) {
      return (
        <a
          key={item.id}
          href={item.href}
          className={className}
          title={item.label}
          target="_blank"
          rel="noopener noreferrer"
        >
          {icon}
          {label}
        </a>
      );
    }

    return (
      <button
        key={item.id}
        type="button"
        className={className}
        title={item.label}
        onClick={() => this.handleItem(item)}
      >
        {icon}
        {label}
      </button>
    );
  };

  renderChild = (item) => {
    const icon = <i className={`wb ${item.icon}`} aria-hidden="true" />;
    const label = <span className="dc-nav-label">{item.label}</span>;
    if (item.to) {
      return (
        <Link key={item.id} to={item.to} className="dc-menu-link" title={item.label}>
          {icon}
          {label}
        </Link>
      );
    }
    return (
      <a
        key={item.id}
        href={item.href}
        className="dc-menu-link"
        title={item.label}
        target="_blank"
        rel="noopener noreferrer"
      >
        {icon}
        {label}
      </a>
    );
  };

  render() {
    const { projectId, version, location } = this.props;
    const pathname = location && location.pathname ? location.pathname : '';
    const developerApiDomain = window.location.hostname.split('.')[0];
    const navigation = getShellNavigation({ projectId, version, developerApiDomain });
    const collapsed = this.context && this.context.sidebarCollapsed;
    const navOpen = this.context && this.context.navOpen;

    return (
      <Fragment>
        {navOpen ? (
          <button
            type="button"
            className="dc-nav-overlay"
            aria-label="Close navigation"
            onClick={this.context.closeNav}
          />
        ) : null}
        <nav className="dc-project-sidebar" aria-label="Project">
          {/* <div className="dc-sidebar-tools">
            <button
              type="button"
              className="dc-icon-btn"
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              onClick={this.context.toggleSidebar}
            >
              <i className="wb webconnect-layout-text-sidebar" aria-hidden="true" />
            </button>
          </div> */}
          <div className="dc-sidebar-scroll">
            {navigation.groups.map((group) => (
              <div key={group.id}>
                <div className="dc-nav-group-label">{group.label}</div>
                {group.items.map((item) => this.renderLink(item, pathname))}
              </div>
            ))}
          </div>
          <div className="dc-sidebar-footer">
            {navigation.footer.map((item) => {
              if (item.children) {
                const open = this.state.openMenu === item.id;
                return (
                  <div key={item.id}>
                    <button
                      type="button"
                      className="dc-nav-group-toggle"
                      title={item.label}
                      aria-expanded={open}
                      onClick={() => this.toggleMenu(item.id)}
                    >
                      <i className={`wb ${item.icon}`} aria-hidden="true" />
                      <span className="dc-nav-label">{item.label}</span>
                    </button>
                    {open ? (
                      <div className="dc-menu-children">
                        {item.children.map((child) => this.renderChild(child))}
                      </div>
                    ) : null}
                  </div>
                );
              }
              return this.renderLink(item, pathname);
            })}
          </div>
          <button
            type="button"
            className="dc-sidebar-resizer"
            aria-label="Resize sidebar"
            title="Drag to resize sidebar"
            onPointerDown={this.startResize}
          />
        </nav>
        <div id="PAGES" className="sidebar">
          <PageTab projectId={projectId} version={version} />
        </div>
        <div id="COLLECTIONS" className="sidebar">
          <CollectionTab projectId={projectId} version={version} />
        </div>
        <div id="TEMPLATES" className="sidebar">
          <SnippetTemplateTab projectId={projectId} version={version} />
        </div>
        <div id="EVENTS" className="sidebar">
          <EventTab projectId={projectId} version={version} />
        </div>
        <div id="ASSETS" className="sidebar">
          <AssetTab projectId={projectId} version={version} />
        </div>
      </Fragment>
    );
  }
}

function mapStateToProps(state) {
  return {
    home: state.home,
    tutorial: state.tutorial,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...actions }, dispatch),
    tutorialActions: bindActionCreators({ ...tutorialActions }, dispatch),
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ProjectSidebar));
